// Wine Tasting FAQ content.
//
// To add or edit an entry: add an object to the `wineTastingFaq` array below.
// Each entry has a `question` string and an `answer` array of blocks.
// A block is either:
//   - a plain string  -> rendered as a paragraph
//   - { items: [...] }            -> rendered as a bulleted list
//   - { ordered: true, items: [...] } -> rendered as a numbered list
// Blocks render in order, so you can mix paragraphs and lists freely.
export const wineTastingFaq = [
    {
        question: 'What should I expect at my first wine tasting?',
        answer: [
            'Most tastings include a flight of 4–6 wines served in small pours, usually guided by a host who explains the winery, grape varieties, and tasting notes. The experience is meant to be relaxed and conversational — not intimidating.',
        ],
    },
    {
        question: 'Do I need to know anything about wine beforehand?',
        answer: [
            'Not at all. Wine tasting is for beginners and enthusiasts alike. You’re not expected to know terminology or identify obscure flavors. The best approach is curiosity and an open mind.',
        ],
    },
    {
        question: 'How much wine do you actually drink?',
        answer: [
            'Pours are typically small — around 1–2 ounces per wine. You’re there to taste, not necessarily finish every glass.',
        ],
    },
    {
        question: 'Is it OK to spit out the wine?',
        answer: [
            'Absolutely. Professional tasters do this all the time, especially when visiting multiple wineries. Most tasting rooms provide a dump bucket or spittoon.',
        ],
    },
    {
        question: 'What should I wear?',
        answer: [
            'Smart casual is usually perfect. Comfortable shoes are a good idea if you’ll be walking vineyards or winery grounds. Avoid strong perfumes or colognes because they interfere with the aromas of the wine.',
        ],
    },
    {
        question: 'Do I need a reservation?',
        answer: [
            'Increasingly, yes — especially at smaller or premium wineries. Reservations help wineries provide a better, more personalized experience.',
        ],
    },
    {
        question: 'How many wineries should I visit in one day?',
        answer: [
            'For the best experience, most wine travelers recommend 2–3 wineries per day. Rushing through too many tastings can become exhausting and makes it harder to remember what you enjoyed.',
        ],
    },
    {
        question: 'Should I eat before wine tasting?',
        answer: [
            'Yes. Wine tasting on an empty stomach is rarely a good idea. A light meal or snacks beforehand will help you pace yourself and enjoy the wines more comfortably.',
        ],
    },
    {
        question: 'What’s the “right” way to taste wine?',
        answer: [
            'Most people follow a simple sequence:',
            {
                ordered: true,
                items: [
                    'Look — notice the color and clarity',
                    'Swirl — helps release aromas',
                    'Smell — aromas are a huge part of tasting',
                    'Sip — notice flavors, texture, and finish',
                ],
            },
            'There’s no need to overthink it.',
        ],
    },
    {
        question: 'Why do people swirl the glass?',
        answer: [
            'Swirling aerates the wine and releases aromas, making it easier to smell and taste subtle characteristics.',
        ],
    },
    {
        question: 'Why do people hold the glass by the stem?',
        answer: [
            'Holding the stem prevents your hand from warming the wine and keeps fingerprints off the bowl so you can better see the wine’s color.',
        ],
    },
    {
        question: 'What if I don’t like a wine everyone else seems to love?',
        answer: [
            'That’s completely normal. Wine preferences are personal. The goal is discovering what you enjoy, not agreeing with everyone else.',
        ],
    },
    {
        question: 'Am I expected to buy wine?',
        answer: [
            'Usually no — though purchasing a bottle is always appreciated if you enjoyed the experience. Some wineries may waive tasting fees with a purchase.',
        ],
    },
    {
        question: 'Should I tip at a winery?',
        answer: [
            'Tipping policies vary, but tipping tasting room staff is increasingly common, especially for seated or personalized tastings.',
        ],
    },
    {
        question: 'What kinds of questions should I ask?',
        answer: [
            'Good questions are simple and genuine:',
            {
                items: [
                    'What wine are you most excited about right now?',
                    'What makes this winery unique?',
                    'Which wine pairs best with food?',
                    'What’s your favorite wine here?',
                ],
            },
            'You don’t need to sound like a sommelier.',
        ],
    },
    {
        question: 'What’s the difference between dry and sweet wine?',
        answer: [
            'Sweet wines contain more residual sugar. Dry wines have little to no noticeable sweetness. Many beginners are surprised that some wines they assumed were sweet are actually dry.',
        ],
    },
    {
        question: 'Do expensive wines always taste better?',
        answer: [
            'Not necessarily. Personal preference matters more than price. Many people prefer approachable, balanced wines over rare or expensive bottles.',
        ],
    },
    {
        question: 'How can I remember wines I liked?',
        answer: [
            'Take photos of labels, keep notes in your phone, or use a wine tracking app, or better yet, take notes in the WineryXplorer app!  Even a quick note like “fruity and smooth” is enough to help later.',
        ],
    },
    {
        question: 'Is wine tasting supposed to feel intimidating?',
        answer: [
            'Not at all. Great wineries focus on hospitality, storytelling, and helping guests discover new wines in a welcoming environment. The best tasting experiences feel more like conversations than classes.',
        ],
    },
    {
        question: 'Can I bring my own food to a wine tasting?',
        answer: [
            'Many wineries will allow you to bring your own food, especially if they don’t sell food on site. It’s always polite to ask first, though.',
        ],
    },
];
